'use client';

import { ReactNode, useEffect, useState } from 'react';
import { Typography } from 'antd';
import { AppError } from '@/lib/error-handler';
import { createClientLogger } from '@/lib/logger/client-logger';

const { Title } = Typography;
import { useRouter } from 'next/navigation';
import { useError } from '@/components/infrastructure/error-context';

// 创建错误边界专用的日志记录器
const logger = createClientLogger('error-boundary');

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
}

/**
 * 错误边界组件
 * 用于捕获子组件树中的JavaScript错误并显示友好的错误界面
 *
 * 注意：在React 16及以上版本中，错误边界仅捕获渲染期间、生命周期方法和构造函数中的错误。
 * 它们不捕获事件处理程序中的错误、异步代码中的错误或服务端渲染期间的错误。
 */
export default function ErrorBoundary({
    children,
    fallback,
}: ErrorBoundaryProps) {
    const [hasError, setHasError] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const router = useRouter();

    // 错误捕获逻辑
    useEffect(() => {
        // 如果不是客户端环境，直接返回
        if (typeof window === 'undefined') return;

        const handleError = (event: ErrorEvent) => {
            event.preventDefault();
            setError(event.error);
            setHasError(true);

            // 安全地记录错误信息，避免访问严格模式下被禁用的属性
            logger.error('全局错误捕获', {
                message: event.error?.message, //错误信息
                stack: event.error?.stack, //错误栈，包含错误发生的位置和调用组件
                lineno: event.lineno, //错误行号
                colno: event.colno, //错误列号
                filename: event.filename, //错误文件名
            });
        };

        window.addEventListener('error', handleError);
        return () => window.removeEventListener('error', handleError);
    }, []);

    // 未处理的Promise拒绝捕获
    useEffect(() => {
        // 如果不是客户端环境，直接返回
        if (typeof window === 'undefined') return;

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const rejectionError =
                (event.reason as Error) ||
                new Error('Unhandled promise rejection');
            setError(rejectionError);
            setHasError(true);

            // 安全地记录错误信息，避免访问严格模式下被禁用的属性
            logger.error('未处理的Promise拒绝', {
                reason: {
                    message: (event.reason as Error)?.message, //拒绝原因的错误信息
                    stack: (event.reason as Error)?.stack, //拒绝原因的错误栈
                    type: (event.reason as Error)?.constructor?.name, //拒绝原因的错误类型
                },
                // 不记录整个promise对象，只记录其是否存在
                promiseExists: !!event.promise,
            });
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        return () =>
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            );
    }, []);

    // 获取错误上下文
    const { setErrorInfo } = useError();

    // 导航到错误页面
    const handleNavigateToErrorPage = () => {
        // 安全地记录错误信息，避免访问严格模式下被禁用的属性
        logger.error('导航到错误页面，错误如下', {
            error: {
                message: error?.message, //错误信息
                stack: error?.stack, //错误栈，包含错误发生的位置和调用组件
                type: error?.constructor?.name, //错误类型
            },
        });
        // 如果是AppError类型，可以提取更多信息
        if (error instanceof AppError) {
            // 特殊处理404错误，导航到专门的not-found页面
            if (error.statusCode === 404) {
                router.push(`/errors/404`);
            } else {
                // 使用错误上下文存储错误信息
                setErrorInfo({
                    code: error.statusCode.toString(),
                    message: error.message,
                });
                router.push(`/errors/500`);
            }
        } else {
            // 使用错误上下文存储错误信息
            setErrorInfo({
                message: error?.message || '发生未知错误',
            });
            router.push(`/errors/500`);
        }
    };

    // 重置错误状态
    const resetError = () => {
        setHasError(false);
        setError(null);
        // 刷新页面以清除任何潜在的错误状态
        router.refresh();
    };

    if (hasError) {
        // 检查是否提供了自定义的回退UI
        if (fallback) {
            return fallback;
        }

        // 默认的错误回退UI
        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'var(--gray-1)',
                    padding: '20px',
                    fontFamily: 'Arial, sans-serif',
                }}
            >
                <div
                    style={{
                        textAlign: 'center',
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '8px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        maxWidth: '400px',
                        width: '100%',
                    }}
                >
                    <Title
                        level={1}
                        style={{
                            color: 'var(--error-color)',
                            marginBottom: '15px',
                        }}
                    >
                        应用程序错误
                    </Title>
                    <p
                        style={{
                            color: 'var(--gray-15)',
                            marginBottom: '25px',
                        }}
                    >
                        {error?.message || '抱歉，发生了一个错误。'}
                    </p>
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            justifyContent: 'center',
                        }}
                    >
                        <button
                            onClick={handleNavigateToErrorPage}
                            style={{
                                backgroundColor: 'var(--error-color)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                            }}
                        >
                            查看错误详情
                        </button>
                        <button
                            onClick={resetError}
                            style={{
                                backgroundColor: 'var(--gray-15)',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px',
                            }}
                        >
                            重置
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 正常渲染子组件
    return children;
}
